import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kDataValueCore } from "../../../Admin/kReusableCoreTS/kDataValueCore";
import { AppSearch } from "./AppSearch";
import { AppIndex, AppRow } from "./AppIndex";
import { AppMoney, AppMoneyAdd, AppMoneyRow, AppMoneySet } from "./AppMoney";
import { AppIcons } from "./AppIcons";
import { AppTaskRules } from "./AppTaskRules";

// AppMoneyModel - the Revenue view as DATA (2026-10-14): entries, filters, totals, groups, the per-contact view, the
// month timeline, "settled this month", and the TABLE SEAM - columns and rows shaped for
// the shared `kEditableTable` (`IkColumnSpec`: `InternalName`, `Header`, `Type`, `Options`, `Locked`) plus the rule
// that turns one cell edit into a `/money` action (`EditOf`). ⚠ PURE: no DOM, no transport - the host
// (`AppMoneyTable`) draws it, `AppMoneyRun` sends what it decides.
// ⛔ A missing field is simply not shown - never a placeholder word.

export class AppMoneyEntry {
    public Id: string = "";
    public Href: string = "";
    public Contact: string = "";
    public Label: string = "";
    public Value: string = "";
    /** Minor units. */
    public Amount: number = 0;
    public Currency: string = "";
    public Direction: string = "";
    public State: string = "open";
    public SettledOn: string = "";
    public Due: string = "";
    public Date: string = "";
    public Anchor: string = "";
    public Source: string = "ai";
    public TaskId: string = "";
    public Corrected: boolean = false;
    /** Whole days since it arose (open items only meaningful); -1 = no date. */
    public AgeDays: number = -1;
    public Overdue: boolean = false;
}

export class AppMoneyFilter {
    /** `open`, `settled` or `all`. */
    public State: string = "open";
    public Currency: string = "";
    public Direction: string = "";
    public From: string = "";
    public To: string = "";
    public Search: string = "";
    /** `items` or `people`. */
    public View: string = "items";
}

export class AppMoneyTotal {
    public Currency: string = "";
    public In: number = 0;
    public Out: number = 0;
}

export class AppMoneyPerson {
    public Href: string = "";
    public Contact: string = "";
    public Totals: AppMoneyTotal[] = [];
    public Open: number = 0;
}

/** One table of items. */
export class AppMoneyGroup {
    public Key: string = "";
    public Title: string = "";
    public Rows: AppMoneyEntry[] = [];
}

/** One column, as `kEditableTable`'s `IkColumnSpec` describes one. `Type`: text, number, date, select. */
export class AppMoneyColumn {
    public InternalName: string = "";
    public Header: string = "";
    public Type: string = "text";
    public Options: string[] = [];
    public Locked: boolean = true;
    /** Pixels, sized to the column's content; the table spreads any room left over across them. */
    public Width: number = 80;
}

/** One cell edit turned into an action, or `Why` (a sentence) when it cannot be. */
export class AppMoneyEdit {
    public Action: string = "";
    public Id: string = "";
    public Set: AppMoneySet | null = null;
    public Why: string = "";
}

/** One contact the add-row may name: the words in its Contact choice and the page it belongs to. */
export class AppMoneyContact {
    public Label: string = "";
    public Href: string = "";
}

/** The add-row turned into a `/money add`, or `Why` + the columns (`InternalName`s) to fix. */
export class AppMoneyNew {
    public Add: AppMoneyAdd | null = null;
    public Href: string = "";
    public Why: string = "";
    public Missing: string[] = [];
}

export class AppMoneyModel {
    public static readonly Months: number = 12;
    /** The columns that may be changed on an existing item (the rest are filled only on the add-row). */
    public static readonly Editable: string[] = ["Amount", "Currency", "Direction", "Due", "State"];
    /** The add-row's required columns, in the table's order, with the words a sentence names them by. */
    public static readonly Required: string[][] = [["Contact", "Contact"], ["Label", "For"], ["Amount", "Amount"],
        ["Currency", "Currency"], ["Direction", "Who owes"]];
    /** The column that takes the table's spare width (a description; the others are sized to their content). */
    public static readonly SlackColumn: string = "Label";
    /** The visible word of a row's message link (its accessible name is `OpenName`). Never "Open" - that is a State. */
    public static readonly OpenWord: string = "View";

    /** The row's message link as drawn: the "go to message" icon (its words are `OpenName`). DOCUMENTED EXCEPTION: constant markup. */
    public static OpenIcon = (): string => {
        return AppIcons.Svg(AppIcons.Go, 16);
    };
    /** The column holding each row's "open the message" control. */
    public static readonly OpenColumn: string = "Open";

    /** ⭐ THE SWITCH: does this index carry money rows with the new fields? DOCUMENTED EXCEPTION: pure check. */
    public static Enabled = (Index: AppIndex): boolean => {
        for (const One of Index.Money) { if (One.MoneyRow !== null) { return true; } }
        return false;
    };

    /** Every entry, from the index's money rows. `Today` = `YYYY-MM-DD` in the browser's day. */
    public static Entries = (Index: AppIndex, Today: string): AppMoneyEntry[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: AppMoneyEntry[] = [];
            for (const One of Index.Money) {
                if (One.MoneyRow === null) { continue; }
                const E: AppMoneyEntry | null = AppMoneyModel.EntryOf(One, One.MoneyRow as AppMoneyRow, Today);
                if (E !== null) { Out.push(E); }
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyModel.Entries");
            return [];
        }
    };

    public static EntryOf = (Row: AppRow, M: AppMoneyRow, Today: string): AppMoneyEntry | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (AppMoney.Directions.indexOf(M.Direction) < 0 || M.Amount <= 0) { return null; }
            const E: AppMoneyEntry = new AppMoneyEntry();
            E.Id = M.Id; E.Href = Row.Href; E.Contact = Row.Contact; E.Label = M.Label; E.Value = M.Value;
            E.Amount = M.Amount; E.Currency = M.Currency; E.Direction = M.Direction; E.State = M.State;
            E.SettledOn = M.SettledOn; E.Due = M.Due; E.Date = M.Date; E.Anchor = M.Anchor; E.Source = M.Source;
            E.TaskId = M.TaskId; E.Corrected = M.Corrected;
            E.AgeDays = AppMoneyModel.DaysBetween(E.Date, Today);
            E.Overdue = E.State === "open" && E.Due.length > 0 && E.Due < nz(Today);
            return E;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyModel.EntryOf");
            return null;
        }
    };

    /** Whole days from `From` to `To` (both `YYYY-MM-DD`), or -1. DOCUMENTED EXCEPTION: pure arithmetic. */
    public static DaysBetween = (From: string, To: string): number => {
        if (AppMoney.DateOk(From) === false || AppMoney.DateOk(To) === false) { return -1; }
        return Math.max(0, Math.round((Date.parse(To + "T00:00:00Z") - Date.parse(From + "T00:00:00Z")) / 86400000));
    };

    /** The entries the filter lets through, oldest first. */
    public static Filtered = (All: AppMoneyEntry[], F: AppMoneyFilter): AppMoneyEntry[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Words: string[] = AppSearch.Fold(F.Search).split(" ").filter(AppMoneyModel.NotEmpty);
            const Out: AppMoneyEntry[] = [];
            for (const E of All) {
                if (F.State !== "all" && E.State !== F.State) { continue; }
                if (F.Currency.length > 0 && E.Currency !== F.Currency) { continue; }
                if (F.Direction.length > 0 && E.Direction !== F.Direction) { continue; }
                if (F.From.length > 0 && (E.Date.length === 0 || E.Date < F.From)) { continue; }
                if (F.To.length > 0 && (E.Date.length === 0 || E.Date > F.To)) { continue; }
                if (AppMoneyModel.Matches(E, Words) === false) { continue; }
                Out.push(E);
            }
            Out.sort(AppMoneyModel.Oldest);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyModel.Filtered");
            return [];
        }
    };

    /** DOCUMENTED EXCEPTION: pure predicate for `filter`. */
    private static NotEmpty = (Word: string): boolean => {
        return Word.length > 0;
    };

    /** Every word in the contact, label or note. DOCUMENTED EXCEPTION: pure check. */
    private static Matches = (E: AppMoneyEntry, Words: string[]): boolean => {
        const Hay: string = AppSearch.Fold(E.Contact + " " + E.Label + " " + E.Value + " " + E.Currency);
        for (const W of Words) { if (Hay.indexOf(W) < 0) { return false; } }
        return true;
    };

    /** Oldest first; undated last. DOCUMENTED EXCEPTION: pure comparator. */
    private static Oldest = (A: AppMoneyEntry, B: AppMoneyEntry): number => {
        if (A.Date === B.Date) { return 0; }
        if (A.Date.length === 0) { return 1; }
        if (B.Date.length === 0) { return -1; }
        if (A.Date < B.Date) { return -1; }
        return 1;
    };

    /**
     * The item groups the view draws, each one table: open = Overdue, Owed to you, You owe (oldest first, empty
     * groups left out); settled / all = one group. `Key` names the table host (`data-wac-mgroup`).
     */
    public static Groups = (Shown: AppMoneyEntry[], F: AppMoneyFilter): AppMoneyGroup[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            if (F.State !== "open") { return AppMoneyModel.NonEmpty([AppMoneyModel.GroupOf("all", "Money", Shown)]); }
            const Late: AppMoneyEntry[] = [];
            const In: AppMoneyEntry[] = [];
            const Out: AppMoneyEntry[] = [];
            for (const E of Shown) {
                if (E.Overdue === true) { Late.push(E); continue; }
                if (E.Direction === "in") { In.push(E); } else { Out.push(E); }
            }
            return AppMoneyModel.NonEmpty([AppMoneyModel.GroupOf("overdue", "Overdue", Late),
                AppMoneyModel.GroupOf("in", "Owed to you", In), AppMoneyModel.GroupOf("out", "You owe", Out)]);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyModel.Groups");
            return [];
        }
    };

    /** DOCUMENTED EXCEPTION: builds a value. */
    private static GroupOf = (Key: string, Title: string, Rows: AppMoneyEntry[]): AppMoneyGroup => {
        const G: AppMoneyGroup = new AppMoneyGroup();
        G.Key = Key; G.Title = Title; G.Rows = Rows;
        return G;
    };

    /** DOCUMENTED EXCEPTION: pure filter. */
    private static NonEmpty = (All: AppMoneyGroup[]): AppMoneyGroup[] => {
        const Out: AppMoneyGroup[] = [];
        for (const G of All) { if (G.Rows.length > 0) { Out.push(G); } }
        return Out;
    };

    /** Unsettled totals per currency (in and out apart - never added together). */
    public static Totals = (Entries: AppMoneyEntry[]): AppMoneyTotal[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const By: Record<string, AppMoneyTotal> = {};
            const Out: AppMoneyTotal[] = [];
            for (const E of Entries) {
                if (E.State !== "open") { continue; }
                let T: AppMoneyTotal | undefined = By[E.Currency];
                if (T === undefined) { T = new AppMoneyTotal(); T.Currency = E.Currency; By[E.Currency] = T; Out.push(T); }
                if (E.Direction === "in") { T.In = T.In + E.Amount; }
                if (E.Direction === "out") { T.Out = T.Out + E.Amount; }
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyModel.Totals");
            return [];
        }
    };

    /** Settled in the month of `Today`, per currency. */
    public static SettledThisMonth = (Entries: AppMoneyEntry[], Today: string): AppMoneyTotal[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Month: string = nz(Today).substring(0, 7);
            const Done: AppMoneyEntry[] = [];
            for (const E of Entries) {
                if (E.State !== "settled" || E.SettledOn.substring(0, 7) !== Month) { continue; }
                const Copy: AppMoneyEntry = Object.assign(new AppMoneyEntry(), E);
                Copy.State = "open";
                Done.push(Copy);
            }
            return AppMoneyModel.Totals(Done);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyModel.SettledThisMonth");
            return [];
        }
    };

    /** One row per person with unsettled money (or any, when the filter shows settled). */
    public static People = (Entries: AppMoneyEntry[]): AppMoneyPerson[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const By: Record<string, AppMoneyEntry[]> = {};
            const Order: string[] = [];
            for (const E of Entries) {
                if (By[E.Href] === undefined) { By[E.Href] = []; Order.push(E.Href); }
                By[E.Href].push(E);
            }
            const Out: AppMoneyPerson[] = [];
            for (const Href of Order) {
                const P: AppMoneyPerson = new AppMoneyPerson();
                P.Href = Href;
                P.Contact = By[Href][0].Contact;
                P.Totals = AppMoneyModel.Totals(By[Href]);
                for (const E of By[Href]) { if (E.State === "open") { P.Open = P.Open + 1; } }
                Out.push(P);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyModel.People");
            return [];
        }
    };

    /** The last `Months` months up to `Today` (`YYYY-MM`). DOCUMENTED EXCEPTION: pure date arithmetic. */
    public static MonthKeys = (Today: string): string[] => {
        const Out: string[] = [];
        let Y: number = parseInt(nz(Today).substring(0, 4), 10);
        let M: number = parseInt(nz(Today).substring(5, 7), 10);
        if (isNaN(Y) === true || isNaN(M) === true) { return Out; }
        for (let I: number = 0; I < AppMoneyModel.Months; I = I + 1) {
            Out.unshift(String(Y) + "-" + ("0" + String(M)).slice(-2));
            M = M - 1;
            if (M === 0) { M = 12; Y = Y - 1; }
        }
        return Out;
    };

    /** Money per month (by the day it arose) in and out, for ONE currency, in WHOLE units (rounded). */
    public static Timeline = (Entries: AppMoneyEntry[], Currency: string, Months: string[]): number[][] => {
        if (getCoreUtils().KErrorMode === true) { return [[], []]; }
        try {
            const In: number[] = [];
            const Out: number[] = [];
            for (let I: number = 0; I < Months.length; I = I + 1) { In.push(0); Out.push(0); }
            for (const E of Entries) {
                if (E.Currency !== Currency) { continue; }
                const At: number = Months.indexOf(E.Date.substring(0, 7));
                if (At < 0) { continue; }
                const Whole: number = AppMoney.Major(E.Amount, E.Currency);
                if (E.Direction === "in") { In[At] = In[At] + Whole; }
                if (E.Direction === "out") { Out[At] = Out[At] + Whole; }
            }
            return [In.map(Math.round), Out.map(Math.round)];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyModel.Timeline");
            return [[], []];
        }
    };

    /** The open balance with one person (`Href`), per currency - for the chat header. */
    public static BalanceFor = (Entries: AppMoneyEntry[], Href: string): AppMoneyTotal[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Mine: AppMoneyEntry[] = [];
            for (const E of Entries) { if (E.Href === Href) { Mine.push(E); } }
            const Out: AppMoneyTotal[] = [];
            for (const T of AppMoneyModel.Totals(Mine)) { if (T.In !== T.Out) { Out.push(T); } }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyModel.BalanceFor");
            return [];
        }
    };

    /** Open, overdue entries (the Today strip). */
    public static Overdue = (Entries: AppMoneyEntry[]): AppMoneyEntry[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: AppMoneyEntry[] = [];
            for (const E of Entries) { if (E.Overdue === true) { Out.push(E); } }
            Out.sort(AppMoneyModel.Oldest);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyModel.Overdue");
            return [];
        }
    };

    /** The currencies in play, sorted. DOCUMENTED EXCEPTION: pure collection. */
    public static Currencies = (Entries: AppMoneyEntry[]): string[] => {
        const Out: string[] = [];
        for (const E of Entries) { if (Out.indexOf(E.Currency) < 0) { Out.push(E.Currency); } }
        return Out.sort();
    };

    // ── The table seam ───────────────────────────────────────────────────────────────────────────

    /**
     * The columns, as `kEditableTable` `IkColumnSpec`s. Contact (a choice among `Contacts`), For and Since are open so
     * the add-row can be filled; on an existing item only `Editable` columns change. `Open` = the row's message link.
     */
    public static Columns = (Contacts: string[]): AppMoneyColumn[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            // `Open` FIRST, then Contact and Amount: on a phone the table scrolls sideways inside its own box, and these three
            // must be what shows. Widths fit the whole row in the pane at 1280 (their sum + the row number < ~850 px).
            return [AppMoneyModel.Col(AppMoneyModel.OpenColumn, " ", "link", [], true, 44),
                AppMoneyModel.Col("Contact", "Contact", "select", Contacts, false, 96),
                AppMoneyModel.Col("Amount", "Amount", "number", [], false, 76),
                AppMoneyModel.Col("Currency", "Currency", "text", [], false, 68),
                AppMoneyModel.Col("Direction", "Who owes", "select", ["Owed to you", "You owe"], false, 108),
                AppMoneyModel.Col("Label", "For", "text", [], false, 80),
                AppMoneyModel.Col("Date", "Since", "date", [], false, 80), AppMoneyModel.Col("Age", "Days open", "number", [], true, 76),
                AppMoneyModel.Col("Due", "Due", "date", [], false, 80), AppMoneyModel.Col("State", "State", "select", ["Open", "Settled"], false, 76)];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyModel.Columns");
            return [];
        }
    };

    /** DOCUMENTED EXCEPTION: builds a value. */
    private static Col = (Name: string, Header: string, Type: string, Options: string[], Locked: boolean, Width: number): AppMoneyColumn => {
        const C: AppMoneyColumn = new AppMoneyColumn();
        C.InternalName = Name; C.Header = Header; C.Type = Type; C.Options = Options; C.Locked = Locked; C.Width = Width;
        return C;
    };

    /** One row's cell values by `InternalName` (display words; "" = not shown). */
    public static Cells = (E: AppMoneyEntry): Record<string, string> => {
        if (getCoreUtils().KErrorMode === true) { return {}; }
        try {
            let Way: string = "You owe";
            if (E.Direction === "in") { Way = "Owed to you"; }
            let Age: string = "";
            if (E.AgeDays >= 0 && E.State === "open") { Age = String(E.AgeDays); }
            let State: string = "Open";
            if (E.State === "settled") { State = "Settled"; }
            return { Contact: E.Contact, Label: E.Label, Amount: AppMoneyModel.Plain(E.Amount, E.Currency), Currency: E.Currency,
                Direction: Way, Date: E.Date, Age: Age, Due: E.Due, State: State, Open: AppMoneyModel.LinkOf(E) };
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyModel.Cells");
            return {};
        }
    };

    /** The item's source message as a route ("" = none), by the one link rule `AppTaskRules.LinkOf`. */
    public static LinkOf = (E: AppMoneyEntry): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Row: AppRow = new AppRow();
            Row.Href = E.Href;
            Row.Anchor = E.Anchor;
            return AppTaskRules.LinkOf(Row);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyModel.LinkOf");
            return "";
        }
    };

    /** "Open the message about <For>" - the row control's accessible name. DOCUMENTED EXCEPTION: pure words. */
    public static OpenName = (Label: string): string => {
        return "Open the message about " + nz(Label);
    };

    /** The add-row's Contact choices: one-to-one contacts with a page, by name; a repeated name gets "(2)", "(3)". */
    public static ContactChoices = (Index: AppIndex): AppMoneyContact[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: AppMoneyContact[] = [];
            const Seen: Record<string, number> = {};
            for (const C of Index.Contacts) {
                if (C.IsGroup === true || C.Href.length === 0 || nz(C.Title).trim().length === 0) { continue; }
                const Name: string = nz(C.Title).trim();
                Seen[Name] = (Seen[Name] || 0) + 1;
                const One: AppMoneyContact = new AppMoneyContact();
                One.Label = Name;
                if (Seen[Name] > 1) { One.Label = Name + " (" + String(Seen[Name]) + ")"; }
                One.Href = C.Href;
                Out.push(One);
            }
            return Out.sort(AppMoneyModel.ByLabel);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyModel.ContactChoices");
            return [];
        }
    };

    /** DOCUMENTED EXCEPTION: pure comparison. */
    private static ByLabel = (A: AppMoneyContact, B: AppMoneyContact): number => {
        return kDataValueCore.NaturalCompare(A.Label, B.Label, undefined);
    };

    /**
     * ⭐ THE ADD-ROW -> ONE `/money add`. `Cells` = the row's values by `InternalName`. Missing required cells are named
     * (validation, shown in the row), a value that does not parse is `AppMoney.AddWhy`'s sentence; nothing is sent then.
     */
    public static AddOf = (Cells: Record<string, string>, Choices: AppMoneyContact[], Today: string): AppMoneyNew => {
        const Out: AppMoneyNew = new AppMoneyNew();
        if (getCoreUtils().KErrorMode === true) { Out.Why = AppMoney.AmountWhy(); return Out; }
        try {
            const Words: string[] = [];
            for (const R of AppMoneyModel.Required) {
                if (nz(Cells[R[0]]).trim().length === 0) { Out.Missing.push(R[0]); Words.push(R[1]); }
            }
            for (const C of Choices) { if (C.Label === nz(Cells.Contact).trim()) { Out.Href = C.Href; } }
            if (Words.length === 0 && Out.Href.length === 0) { Out.Missing.push("Contact"); Words.push("Contact"); }
            if (Words.length > 0) { Out.Why = "Fill in " + Words.join(", ") + " to add this item."; return Out; }
            const A: AppMoneyAdd = AppMoneyModel.AddFrom(Cells, Today);
            Out.Why = AppMoney.AddWhy({ label: A.Label, amount: A.Amount, currency: A.Currency, direction: A.Direction, due: A.Due, date: A.Date });
            if (Out.Why.length > 0) { return Out; }
            Out.Add = A;
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyModel.AddOf");
            Out.Why = AppMoney.AmountWhy();
            return Out;
        }
    };

    /** The typed words as an `AppMoneyAdd` (checked by `AddOf`). DOCUMENTED EXCEPTION: pure mapping. */
    private static AddFrom = (Cells: Record<string, string>, Today: string): AppMoneyAdd => {
        const A: AppMoneyAdd = new AppMoneyAdd();
        A.Label = nz(Cells.Label).trim();
        A.Currency = nz(Cells.Currency).trim().toUpperCase();
        A.Amount = AppMoney.MinorOf(nz(Cells.Amount).trim(), A.Currency);
        A.Direction = "out";
        if (nz(Cells.Direction).trim() === "Owed to you") { A.Direction = "in"; }
        A.Due = nz(Cells.Due).trim();
        A.Date = nz(Cells.Date).trim();
        if (A.Date.length === 0) { A.Date = nz(Today); }
        return A;
    };

    /**
     * Minor units as a plain WHOLE number ("1251") - money is never shown with decimals (owner review 2026-11-01). Not grouped:
     * it is the table's editable number cell, read back by `AppMoney.MinorOf`. DOCUMENTED EXCEPTION: pure formatting.
     */
    public static Plain = (Minor: number, Currency: string): string => {
        return Math.round(AppMoney.Major(Minor, Currency)).toFixed(0);
    };

    /**
     * ⭐ ONE CELL EDIT -> ONE `/money` ACTION (the seam `kEditableTable`'s edit callback calls). Only the four editable
     * columns; a value that does not parse is a sentence, never sent.
     */
    public static EditOf = (E: AppMoneyEntry, Column: string, Typed: string): AppMoneyEdit => {
        const Out: AppMoneyEdit = new AppMoneyEdit();
        if (getCoreUtils().KErrorMode === true) { Out.Why = AppMoney.AmountWhy(); return Out; }
        try {
            Out.Action = "correct";
            Out.Id = E.Id;
            const Set: AppMoneySet = new AppMoneySet();
            const T: string = nz(Typed).trim();
            if (Column === "Amount") { Set.Amount = AppMoney.MinorOf(T, E.Currency); if (Set.Amount < 0) { Out.Why = AppMoney.AmountWhy(); } }
            else if (Column === "Currency") {
                Set.Currency = T.toUpperCase();
                if (AppMoney.CurrencyPattern.test(Set.Currency) === false) { Out.Why = "Choose the currency as its three-letter code, like EUR or SEK."; }
            } else if (Column === "Direction") {
                Set.Direction = "out";
                if (T === "Owed to you" || T === "in") { Set.Direction = "in"; }
            } else if (Column === "Due") {
                Set.Due = T;
                if (AppMoney.DateOk(T) === false) { Out.Why = "Choose a real date for when it is due."; }
            } else if (Column === "State") {
                Out.Action = "reopen";
                if (T === "Settled") { Out.Action = "settle"; }
                Out.Set = null;
                if ((Out.Action === "settle") === (E.State === "settled")) { Out.Why = "That item already stands so."; }
                return Out;
            } else { Out.Why = "That column cannot be changed."; }
            Out.Set = Set;
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyModel.EditOf");
            Out.Why = AppMoney.AmountWhy();
            return Out;
        }
    };
}
